import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Calendar, Copy, RefreshCw, Loader2, Check, ExternalLink, Smartphone, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function CalendarSync() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenLoading, setRegenLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchInfo = async () => {
    setLoading(true);
    try {
      const r = await api.get("/calendar/me");
      setInfo(r.data);
    } catch { toast.error("Erreur"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchInfo(); }, []);

  const fullUrl = (() => {
    if (!info) return "";
    if (info.feed_url && info.feed_url.startsWith("http")) return info.feed_url;
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${base.replace(/\/$/, "")}${info.feed_path}`;
  })();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success("URL copiée");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Impossible de copier"); }
  };

  const regenerate = async () => {
    if (!window.confirm("Régénérer l'URL ? L'ancienne URL ne fonctionnera plus, vous devrez réabonner votre calendrier.")) return;
    setRegenLoading(true);
    try {
      const r = await api.post("/calendar/me/regenerate");
      setInfo(r.data);
      toast.success("Nouvelle URL générée");
    } catch { toast.error("Erreur"); }
    finally { setRegenLoading(false); }
  };

  const googleAddUrl = `https://calendar.google.com/calendar/r/settings/addbyurl`;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra font-semibold mb-2">Synchronisation</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
          <Calendar className="text-btp-terra" /> Calendrier
        </h1>
        <p className="text-sm text-slate-500 mt-1">Synchronisez vos missions, tâches et jalons projets dans Google Calendar, Apple Calendar ou Outlook.</p>
      </div>

      {loading ? (
        <div className="text-slate-400">Chargement…</div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1.5">URL d'abonnement (privée)</div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={fullUrl}
                  className="flex-1 h-11 px-3 border border-slate-300 rounded text-xs font-mono bg-slate-50 focus:bg-white"
                  onFocus={(e) => e.target.select()}
                  data-testid="calendar-url"
                />
                <button onClick={copy} className="h-11 px-3 border border-slate-300 rounded text-sm font-semibold flex items-center gap-2 hover:bg-slate-50" data-testid="calendar-copy">
                  {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                  {copied ? "Copié" : "Copier"}
                </button>
              </div>
              <div className="mt-2 flex items-start gap-2 text-xs text-slate-500">
                <AlertCircle size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <span>Cette URL contient un jeton secret. Ne la partagez pas — toute personne disposant du lien peut consulter votre calendrier.</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              <a href={googleAddUrl} target="_blank" rel="noreferrer"
                className="bg-btp-navy hover:bg-btp-navy-hover text-white px-3 h-10 rounded text-sm font-semibold flex items-center gap-2"
                data-testid="open-google-calendar">
                <ExternalLink size={14} /> Ouvrir Google Calendar
              </a>
              <a href={fullUrl}
                className="border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 h-10 rounded text-sm font-semibold flex items-center gap-2"
                data-testid="open-ics">
                <Smartphone size={14} /> Ouvrir dans calendrier (iOS/Mac)
              </a>
              <button onClick={regenerate} disabled={regenLoading}
                className="ml-auto border border-amber-300 hover:bg-amber-50 text-amber-800 px-3 h-10 rounded text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                data-testid="calendar-regenerate">
                {regenLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Régénérer l'URL
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Step n={1} title="Google Calendar">
              <ol className="list-decimal pl-4 space-y-1.5 text-sm text-slate-700">
                <li>Ouvrez Google Calendar sur ordinateur</li>
                <li>À gauche, cliquez <span className="font-semibold">"Autres agendas"</span> → <span className="font-semibold">+</span></li>
                <li>Choisissez <span className="font-semibold">"À partir de l'URL"</span></li>
                <li>Collez l'URL ci-dessus → <span className="font-semibold">"Ajouter l'agenda"</span></li>
              </ol>
              <div className="mt-3 text-xs text-slate-500">Google rafraîchit toutes les ~12h.</div>
            </Step>
            <Step n={2} title="Apple Calendar (Mac/iOS)">
              <ol className="list-decimal pl-4 space-y-1.5 text-sm text-slate-700">
                <li>Mac : Calendrier → <span className="font-semibold">Fichier</span> → <span className="font-semibold">Nouvel abonnement…</span></li>
                <li>iOS : Réglages → Calendrier → Comptes → <span className="font-semibold">Ajouter un compte</span> → Autre → <span className="font-semibold">Ajouter un compte CalDAV</span></li>
                <li>Collez l'URL → <span className="font-semibold">S'abonner</span></li>
              </ol>
            </Step>
            <Step n={3} title="Outlook">
              <ol className="list-decimal pl-4 space-y-1.5 text-sm text-slate-700">
                <li>Outlook web : <span className="font-semibold">Ajouter un calendrier</span></li>
                <li>Choisissez <span className="font-semibold">"S'abonner depuis le Web"</span></li>
                <li>Collez l'URL ci-dessus</li>
                <li>Donnez un nom → <span className="font-semibold">Importer</span></li>
              </ol>
            </Step>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-md p-5">
            <h3 className="font-heading font-bold text-sm mb-2">Que contient le flux ?</h3>
            <ul className="space-y-1.5 text-sm text-slate-700">
              <li className="flex gap-2"><span>🚧</span> <span><strong>Missions terrain</strong> où vous êtes affecté(e)</span></li>
              <li className="flex gap-2"><span>✅</span> <span><strong>Tâches</strong> avec une échéance qui vous sont assignées</span></li>
              <li className="flex gap-2"><span>📋</span> <span><strong>Jalons projets</strong> (début/fin) — visibles pour Admin et Chef de projet</span></li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-btp-navy text-white flex items-center justify-center text-xs font-bold">{n}</div>
        <h3 className="font-heading font-bold text-base">{title}</h3>
      </div>
      {children}
    </div>
  );
}
